import { useState } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { Typography, Autocomplete, TextField, Dialog } from '@mui/material'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { Paragraph } from 'src/feature/localization/component/atom/Paragraph'
import { PerformanceReviewTemplate } from '../../model/my-team-performance'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

type CreateTemplateModalProps = {
  open: boolean
  onClose: () => void
  templates: PerformanceReviewTemplate[]
  onUseTemplate: (template: PerformanceReviewTemplate) => void
  onStartManually: () => void
}

export function CreateTemplateModal({ open, onClose, templates, onUseTemplate, onStartManually }: CreateTemplateModalProps) {
  const [step, setStep] = useState<'choose' | 'reuse'>('choose')
  const [sourceTemplate, setSourceTemplate] = useState<PerformanceReviewTemplate | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const { translation } = useLocalizationStore()
  const performanceReviewTranslation = translation.pages.myTeam.performanceReview

  const templatesWithQuestions = templates.filter(t => t.questions && t.questions.length > 0)

  const handleClose = () => {
    onClose()
    setStep('choose')
    setSourceTemplate(null)
    setShowPreview(false)
  }

  const handleUse = () => {
    if (!sourceTemplate) {
      return
    }

    onUseTemplate(sourceTemplate)
    handleClose()
  }

  return (
    <>
      <ModalWindow
        modalProps={{
          open,
          onClose: handleClose,
        }}
        titleProps={{
          textValue: step === 'choose'
            ? performanceReviewTranslation.createTemplateTitle()
            : performanceReviewTranslation.reuseTemplate(),
        }}
      >
        <AppBox sx={styles.content}>
          {step === 'choose' && (
            <>
              <Paragraph
                textValue={performanceReviewTranslation.createTemplateDescription()}
              />

              <AppDivider />
              <AppBox sx={styles.actions}>
                <AppBox sx={styles.option}>
                  <Typography sx={styles.optionTitle}>
                    {performanceReviewTranslation.startManually()}
                  </Typography>

                  <Typography sx={styles.optionDescription}>
                    {performanceReviewTranslation.startManuallyDescription()}
                  </Typography>

                  <AppButton
                    variant='contained'
                    fontSize={12}
                    onClick={() => {
                      onStartManually()
                      handleClose()
                    }}
                  >
                    {performanceReviewTranslation.startManually()}
                  </AppButton>
                </AppBox>

                <AppBox sx={styles.option}>
                  <Typography sx={styles.optionTitle}>
                    {performanceReviewTranslation.reuseTemplate()}
                  </Typography>

                  <Typography sx={styles.optionDescription}>
                    {performanceReviewTranslation.reuseTemplateDescription()}
                  </Typography>

                  <AppButton
                    variant='light'
                    fontSize={12}
                    onClick={() => setStep('reuse')}
                  >
                    {performanceReviewTranslation.chooseTemplate()}
                  </AppButton>
                </AppBox>
              </AppBox>
            </>
          )}

          {step === 'reuse' && (
            <>
              <Paragraph
                textValue={performanceReviewTranslation.chooseTemplateDescription()}
              />

              <Autocomplete
                fullWidth
                size='small'
                options={templatesWithQuestions}
                getOptionLabel={option => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={sourceTemplate}
                onChange={(_, value) => setSourceTemplate(value)}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={performanceReviewTranslation.searchTemplates()}
                  />
                )}
              />

              <AppBox sx={styles.actions}>
                <AppButton
                  variant='light'
                  fontSize={12}
                  onClick={() => {
                    setStep('choose')
                    setSourceTemplate(null)
                  }}
                >
                  {performanceReviewTranslation.back()}
                </AppButton>

                <AppButton
                  variant='light'
                  fontSize={12}
                  disabled={!sourceTemplate}
                  onClick={() => sourceTemplate && setShowPreview(true)}
                >
                  {performanceReviewTranslation.preview()}
                </AppButton>

                <AppButton
                  variant='contained'
                  fontSize={12}
                  disabled={!sourceTemplate}
                  onClick={() => sourceTemplate && handleUse()}
                >
                  {performanceReviewTranslation.useTemplate()}
                </AppButton>
              </AppBox>
            </>
          )}

        </AppBox>
      </ModalWindow>

      {showPreview && (
        <Dialog
          open={showPreview}
          onClose={() => setShowPreview(false)}
          PaperProps={{ sx: styles.previewPaper }}
        >
          <Typography sx={styles.previewTitle}>{sourceTemplate?.name}</Typography>
          <AppBox sx={styles.previewList}>
            {sourceTemplate?.questions?.map((q, idx) => (
              <AppBox key={q.id} sx={styles.previewItem}>
                <Typography sx={styles.previewItemNum}>{idx + 1}.</Typography>
                <Typography sx={styles.previewItemText}>{q.questionText}</Typography>
              </AppBox>
            ))}
          </AppBox>
        </Dialog>
      )}
    </>
  )
}

const styles = {
  content: {
    flexDirection: 'column',
    gap: 10,
  },
  option: {
    flex: 1,
    flexDirection: 'column',
    gap: 5,
    alignItems: 'flex-start',
  },
  optionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: ColorTokens.secondary500,
  },
  optionDescription: {
    fontSize: 12,
    color: 'text.secondary',
    marginBottom: 5,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
    marginTop: 5,
  },
  previewPaper: {
    padding: '24px 28px',
    borderRadius: '14px',
    minWidth: 360,
    maxWidth: 480,
  },
  previewTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: ColorTokens.secondary500,
    marginBottom: 12,
  },
  previewList: {
    flexDirection: 'column',
    gap: 10,
  },
  previewItem: {
    flexDirection: 'row',
    gap: 8,
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: ColorTokens.border100,
  },
  previewItemNum: {
    fontSize: 12,
    fontWeight: 700,
    color: ColorTokens.secondary500,
  },
  previewItemText: {
    fontSize: 13,
    color: ColorTokens.secondary500,
  },
}
